import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Search, User, ChevronDown, LogOut, Film, Plus, Building2, Crown, CreditCard, Tv, TrendingUp, List, Menu, Package, Star, HelpCircle, X, Mail } from 'lucide-react';
import defaultAvatar from '../assets/default-avatar.svg';
import { useAuth } from '../contexts/AuthContext';
import { CartDropdown } from './CartDropdown';
import { filmsApi, creatorsApi, thumbUrl } from '../lib/api';
import CreatorProfileModal from './CreatorProfileModal';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

const GENRES = [
  'Action', 'Adventure', 'Animation', 'Comedy', 'Crime',
  'Documentary', 'Drama', 'Fantasy', 'Horror', 'Mystery',
  'Romance', 'Sci-Fi', 'Thriller', 'Western',
];

const NavDropdown = ({ label, icon: Icon, basePath, items }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const closeTimer = useRef(null);

  // Determine active state from pathname
  const isActive = location.pathname === basePath || location.pathname.startsWith(basePath + '/');
  const currentGenre = new URLSearchParams(location.search).get('genre');

  // Close on outside click
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const handleMouseEnter = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpen(true);
  };

  const handleMouseLeave = () => {
    closeTimer.current = setTimeout(() => setOpen(false), 200);
  };

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen((prev) => !prev);
  };

  const handleNavigate = (path) => {
    navigate(path);
    setOpen(false);
  };

  return (
    <div
      ref={ref}
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        onClick={handleClick}
        className={`text-sm font-medium transition-colors hover:text-white flex items-center gap-1.5 py-1 ${
          isActive
            ? 'text-white border-b-2 border-[#8B5CF6]'
            : 'text-gray-400 border-b-2 border-transparent'
        }`}
        data-testid={`nav-${label.toLowerCase()}`}
      >
        <Icon className="w-4 h-4" />
        {label}
        <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          className="absolute top-full left-0 mt-2 w-56 rounded-lg border border-[#8B5CF6]/20 bg-[#0F0B1E]/95 backdrop-blur-xl shadow-2xl z-50 py-2"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <button
            onClick={() => handleNavigate(basePath)}
            className={`w-full text-left px-4 py-2 text-sm font-medium transition-colors ${
              isActive && !currentGenre
                ? 'text-[#8B5CF6] bg-[#8B5CF6]/10'
                : 'text-white hover:bg-[#8B5CF6]/15'
            }`}
            data-testid={`nav-all-${label.toLowerCase()}`}
          >
            All {label}
          </button>
          <div className="h-px bg-[#8B5CF6]/10 mx-2 my-1" />
          <div className="max-h-64 overflow-y-scroll dropdown-scrollbar">
            {items.map((item) => {
              const isGenreActive = isActive && currentGenre === item;
              return (
                <button
                  key={item}
                  onClick={() => handleNavigate(`${basePath}?genre=${encodeURIComponent(item)}`)}
                  className={`w-full text-left px-4 py-1.5 text-sm transition-colors ${
                    isGenreActive
                      ? 'text-[#8B5CF6] bg-[#8B5CF6]/10 font-medium'
                      : 'text-gray-300 hover:text-white hover:bg-[#8B5CF6]/10'
                  }`}
                  data-testid={`nav-genre-${item.toLowerCase().replace(/\s/g, '-')}`}
                >
                  {item}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState({ creators: [], films: [] });
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedCreator, setSelectedCreator] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const { user, logout, isOwner, isOwnerOrAdmin, isAdminRole, isCompany, isApprovedCreator, companyStatus, hasSubscription, isPremium } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const debounceRef = useRef(null);
  const searchContainerRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close suggestions on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Debounced autocomplete
  const fetchSuggestions = useCallback((q) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q || q.length < 2) {
      setSuggestions({ creators: [], films: [] });
      setShowSuggestions(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const [creatorsData, filmsData] = await Promise.all([
          creatorsApi.search(q),
          filmsApi.getAll({ search: q, limit: 4 }),
        ]);
        const c = (creatorsData.creators || []).slice(0, 2);
        const f = (filmsData || []).slice(0, 6 - c.length);
        setSuggestions({ creators: c, films: f });
        setShowSuggestions(c.length > 0 || f.length > 0);
      } catch {
        setShowSuggestions(false);
      }
    }, 250);
  }, []);

  const handleQueryChange = (e) => {
    const v = e.target.value;
    setSearchQuery(v);
    fetchSuggestions(v);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchOpen(false);
      setSearchQuery('');
      setShowSuggestions(false);
      setSuggestions({ creators: [], films: [] });
    }
  };

  const handleSuggestionClick = (type, item) => {
    setShowSuggestions(false);
    setSearchQuery('');
    setSearchOpen(false);
    setSuggestions({ creators: [], films: [] });
    if (type === 'creator') {
      setSelectedCreator(item.creator);
    } else {
      navigate(`/film/${item.film_id}`);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const isHome = location.pathname === '/browse' && !location.search;

  return (
    <>
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'nav-glass' : 'bg-gradient-to-b from-black/80 to-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-8 lg:px-[4%]">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/browse" className="flex items-center gap-2" data-testid="logo">
            <img src="/reframes-logo.png" alt="reframes" className="h-9 md:h-11" />
          </Link>

          {/* Navigation Links - Desktop */}
          <div className="hidden lg:flex items-center gap-6">
            <Link
              to="/browse"
              data-testid="nav-home"
              className={`text-sm font-medium transition-colors hover:text-white py-1 ${
                isHome
                  ? 'text-white border-b-2 border-[#8B5CF6]'
                  : 'text-gray-400 border-b-2 border-transparent'
              }`}
            >
              Home
            </Link>

            <NavDropdown
              label="Movies"
              icon={Film}
              items={GENRES}
              basePath="/browse/films"
            />

            <NavDropdown
              label="Series"
              icon={Tv}
              items={GENRES}
              basePath="/browse/series"
            />

            <Link
              to="/browse?section=top10"
              data-testid="nav-top-10"
              className={`text-sm font-medium transition-colors hover:text-white flex items-center gap-1.5 py-1 ${
                location.search.includes('section=top10')
                  ? 'text-white border-b-2 border-[#8B5CF6]'
                  : 'text-gray-400 border-b-2 border-transparent'
              }`}
            >
              <TrendingUp className="w-4 h-4" />
              Top 10
            </Link>

            <Link
              to="/my-list"
              data-testid="nav-my-list"
              className={`text-sm font-medium transition-colors hover:text-white flex items-center gap-1.5 py-1 ${
                location.pathname === '/my-list'
                  ? 'text-white border-b-2 border-[#8B5CF6]'
                  : 'text-gray-400 border-b-2 border-transparent'
              }`}
            >
              <List className="w-4 h-4" />
              My List
            </Link>

            {isOwnerOrAdmin && (
              <Link
                to="/owner"
                data-testid="nav-owner"
                className={`text-sm font-medium transition-colors hover:text-[#00FF9D] flex items-center gap-1 ${
                  location.pathname === '/owner' ? 'text-[#00FF9D]' : 'text-gray-400'
                }`}
              >
                <Crown className="w-4 h-4" />
                {isOwner ? 'Owner' : 'Admin'}
              </Link>
            )}
            {isApprovedCreator && (
              <Link
                to="/company/dashboard"
                data-testid="nav-company"
                className={`text-sm font-medium transition-colors hover:text-blue-400 flex items-center gap-1 ${
                  location.pathname.startsWith('/company') ? 'text-blue-400' : 'text-gray-400'
                }`}
              >
                <Building2 className="w-4 h-4" />
                Studio
              </Link>
            )}
            {companyStatus === 'pending' && (
              <span
                data-testid="nav-pending-status"
                className="text-sm font-medium text-amber-400 flex items-center gap-1.5 py-1 px-3 bg-amber-500/10 rounded-full border border-amber-500/20"
              >
                <Building2 className="w-4 h-4" />
                Pending Review
              </span>
            )}
          </div>

          {/* Right Section */}
          <div className="flex items-center gap-1.5 sm:gap-4">
            {/* Search */}
            <div className="relative" ref={searchContainerRef}>
              {searchOpen ? (
                <>
                  <form onSubmit={handleSearch} className="flex items-center">
                    <input
                      type="text"
                      placeholder="Search films & creators..."
                      value={searchQuery}
                      onChange={handleQueryChange}
                      autoFocus
                      data-testid="search-input"
                      className="search-input w-48 md:w-64"
                      onFocus={() => searchQuery.length >= 2 && (suggestions.creators.length > 0 || suggestions.films.length > 0) && setShowSuggestions(true)}
                      onBlur={() => { if (!searchQuery) { setSearchOpen(false); setShowSuggestions(false); } }}
                    />
                    <Search className="absolute left-3 w-4 h-4 text-gray-400" />
                  </form>

                  {/* Autocomplete Dropdown */}
                  {showSuggestions && (
                    <div className="absolute top-full left-0 right-0 mt-1.5 bg-[#0F0B1E]/98 border border-[#8B5CF6]/20 rounded-xl shadow-2xl shadow-black/60 overflow-hidden z-50 backdrop-blur-xl"
                      data-testid="search-suggestions">
                      {suggestions.creators.map(c => (
                        <button key={c.creator}
                          onMouseDown={() => handleSuggestionClick('creator', c)}
                          className="w-full flex items-center gap-3 px-3.5 py-2.5 hover:bg-[#8B5CF6]/10 transition-colors text-left"
                          data-testid={`suggestion-creator-${c.creator}`}>
                          <div className="w-7 h-7 rounded-full bg-[#8B5CF6]/20 flex items-center justify-center shrink-0">
                            <User className="w-3.5 h-3.5 text-[#8B5CF6]" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{c.creator}</p>
                            <p className="text-[10px] text-gray-500 flex items-center gap-1">
                              <Star className="w-2 h-2 fill-[#00FF9D] text-[#00FF9D]" /> {c.avg_rating?.toFixed(1)} · {c.content_count} title{c.content_count !== 1 ? 's' : ''}
                            </p>
                          </div>
                          <span className="text-[9px] text-[#8B5CF6] bg-[#8B5CF6]/10 px-1.5 py-0.5 rounded-full font-medium shrink-0">Creator</span>
                        </button>
                      ))}
                      {suggestions.creators.length > 0 && suggestions.films.length > 0 && (
                        <div className="h-px bg-white/5 mx-3" />
                      )}
                      {suggestions.films.map(f => (
                        <button key={f.film_id}
                          onMouseDown={() => handleSuggestionClick('film', f)}
                          className="w-full flex items-center gap-3 px-3.5 py-2 hover:bg-[#8B5CF6]/10 transition-colors text-left"
                          data-testid={`suggestion-film-${f.film_id}`}>
                          <div className="w-10 h-6 rounded overflow-hidden shrink-0 bg-white/5">
                            <img src={thumbUrl(f.thumbnail, 160)} alt="" className="w-full h-full object-cover" loading="lazy" />
                          </div>
                          <p className="text-sm truncate flex-1 min-w-0">{f.title}</p>
                          <span className="text-[9px] text-gray-500 shrink-0">{f.content_type === 'series' ? 'Series' : 'Movie'}</span>
                        </button>
                      ))}
                      <button onMouseDown={handleSearch}
                        className="w-full px-3.5 py-2 text-xs text-[#8B5CF6] hover:bg-[#8B5CF6]/5 transition-colors text-left flex items-center gap-2 border-t border-white/5"
                        data-testid="suggestion-see-all">
                        <Search className="w-3 h-3" /> See all results for "{searchQuery}"
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <button
                  onClick={() => setSearchOpen(true)}
                  data-testid="search-button"
                  className="p-2 hover:bg-white/10 rounded-full transition-colors"
                >
                  <Search className="w-5 h-5" />
                </button>
              )}
            </div>

            {/* Help */}
            <button onClick={() => setShowHelp(true)} className="p-2 hover:bg-white/10 rounded-full transition-colors" data-testid="help-button" title="Help">
              <HelpCircle className="w-5 h-5 text-gray-300" />
            </button>

            {/* Cart */}
            <CartDropdown />

            {/* User Menu */}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                    data-testid="user-menu-trigger"
                  >
                    <img
                      src={user.picture || defaultAvatar}
                      alt={user.name}
                      className="profile-avatar"
                    />
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-[#0F0B1E] border-[#8B5CF6]/20">
                  <div className="px-3 py-2">
                    <p className="text-sm font-medium text-white">{user.name}</p>
                    <p className="text-xs text-gray-400">{user.email}</p>
                    <div className="flex gap-2 mt-2">
                      {isOwner && (
                        <span className="badge text-xs bg-[#00FF9D]/20 text-[#00FF9D] border-[#00FF9D]/30">Owner</span>
                      )}
                      {isAdminRole && (
                        <span className="badge text-xs bg-[#00FF9D]/20 text-[#00FF9D] border-[#00FF9D]/30">Admin</span>
                      )}
                      {isCompany && (
                        <span className="badge text-xs bg-blue-500/20 text-blue-400 border-blue-500/30">Company</span>
                      )}
                      {companyStatus === 'pending' && (
                        <span className="badge text-xs bg-amber-500/20 text-amber-400 border-amber-500/30" data-testid="pending-badge">Pending</span>
                      )}
                      {hasSubscription && (
                        <span className="badge text-xs bg-[#8B5CF6]/20 text-[#8B5CF6]">Premium</span>
                      )}
                      {!hasSubscription && !isOwnerOrAdmin && (
                        <span className="badge text-xs bg-white/10 text-gray-400">Free</span>
                      )}
                    </div>
                  </div>
                  <DropdownMenuSeparator className="bg-[#8B5CF6]/20" />
                  <DropdownMenuItem asChild>
                    <Link to="/profile" className="flex items-center gap-2 cursor-pointer" data-testid="profile-link">
                      <User className="w-4 h-4" />
                      Profile
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/my-list" className="flex items-center gap-2 cursor-pointer" data-testid="my-list-link">
                      <Plus className="w-4 h-4" />
                      My List
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/my-orders" className="flex items-center gap-2 cursor-pointer" data-testid="my-orders-link">
                      <Package className="w-4 h-4" />
                      My Orders
                    </Link>
                  </DropdownMenuItem>
                  {!isPremium && !isOwnerOrAdmin && (
                    <DropdownMenuItem asChild>
                      <Link to="/pricing" className="flex items-center gap-2 cursor-pointer text-[#00FF9D]" data-testid="subscribe-link">
                        <CreditCard className="w-4 h-4" />
                        Go Premium (No Ads)
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {isPremium && (
                    <DropdownMenuItem disabled className="flex items-center gap-2 text-[#8B5CF6] opacity-70">
                      <CreditCard className="w-4 h-4" />
                      Premium Member
                    </DropdownMenuItem>
                  )}
                  {isOwnerOrAdmin && (
                    <DropdownMenuItem asChild>
                      <Link to="/owner" className="flex items-center gap-2 cursor-pointer text-[#00FF9D]" data-testid="owner-link">
                        <Crown className="w-4 h-4" />
                        {isOwner ? 'Owner Dashboard' : 'Admin Dashboard'}
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {isApprovedCreator && (
                    <>
                      <DropdownMenuItem asChild>
                        <Link to="/company/dashboard" className="flex items-center gap-2 cursor-pointer text-blue-400" data-testid="studio-link">
                          <Building2 className="w-4 h-4" />
                          Creator Dashboard
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/browse" className="flex items-center gap-2 cursor-pointer" data-testid="browse-streaming-link">
                          <Tv className="w-4 h-4" />
                          Browse Streaming
                        </Link>
                      </DropdownMenuItem>
                    </>
                  )}
                  {companyStatus === 'pending' && (
                    <DropdownMenuItem disabled className="flex items-center gap-2 text-amber-400 opacity-80" data-testid="pending-status-menu">
                      <Building2 className="w-4 h-4" />
                      Application Pending
                    </DropdownMenuItem>
                  )}
                  {!isCompany && !isOwnerOrAdmin && (
                    <DropdownMenuItem asChild>
                      <Link to="/company/register" className="flex items-center gap-2 cursor-pointer" data-testid="register-company-link">
                        <Building2 className="w-4 h-4" />
                        Become a Creator
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator className="bg-[#8B5CF6]/20" />
                  <DropdownMenuItem
                    onClick={handleLogout}
                    className="flex items-center gap-2 cursor-pointer text-red-400 focus:text-red-400"
                    data-testid="logout-button"
                  >
                    <LogOut className="w-4 h-4" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link
                to="/login"
                data-testid="login-link"
                className="btn-primary text-sm"
              >
                Sign In
              </Link>
            )}

            {/* Mobile Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild className="lg:hidden">
                <button className="p-2 hover:bg-white/10 rounded-full transition-colors" data-testid="mobile-menu-trigger">
                  <Menu className="w-5 h-5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 bg-[#0F0B1E] border-[#8B5CF6]/20 lg:hidden">
                <DropdownMenuItem asChild>
                  <Link to="/browse" className="cursor-pointer">Home</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/browse/films" className="cursor-pointer flex items-center gap-2">
                    <Film className="w-4 h-4" /> Movies
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/browse/series" className="cursor-pointer flex items-center gap-2">
                    <Tv className="w-4 h-4" /> Series
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/my-list" className="cursor-pointer">My List</Link>
                </DropdownMenuItem>
                {isOwnerOrAdmin && (
                  <DropdownMenuItem asChild>
                    <Link to="/owner" className="cursor-pointer text-[#00FF9D]">{isOwner ? 'Owner Dashboard' : 'Admin Dashboard'}</Link>
                  </DropdownMenuItem>
                )}
                {isApprovedCreator && (
                  <>
                    <DropdownMenuItem asChild>
                      <Link to="/company/dashboard" className="cursor-pointer text-blue-400">Creator Dashboard</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/browse" className="cursor-pointer">Browse Streaming</Link>
                    </DropdownMenuItem>
                  </>
                )}
                {companyStatus === 'pending' && (
                  <DropdownMenuItem disabled className="text-amber-400 opacity-80" data-testid="mobile-pending-status">
                    Application Pending
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </nav>

    {selectedCreator && (
      <CreatorProfileModal
        creatorName={selectedCreator}
        onClose={() => setSelectedCreator(null)}
      />
    )}

    {/* Help Dialog */}
    <Dialog open={showHelp} onOpenChange={setShowHelp}>
      <DialogContent className="bg-[#0F0B1E] border-white/10 max-w-md" data-testid="help-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-[#8B5CF6]" /> Reframes Support
          </DialogTitle>
        </DialogHeader>
        <div className="mt-4 space-y-4">
          <p className="text-gray-300 text-sm leading-relaxed">
            Hey there! <span className="text-lg">👋</span>
          </p>
          <p className="text-gray-300 text-sm leading-relaxed">
            Welcome to Reframes support. We're here to help you get the most out of the platform.
          </p>
          <p className="text-gray-300 text-sm leading-relaxed">
            Reach out to us at the email below for any issues.
          </p>
          <a href="mailto:support@reframes.io" className="flex items-center gap-2 text-[#8B5CF6] hover:text-[#A78BFA] font-medium transition-colors" data-testid="help-email-link">
            <Mail className="w-4 h-4" /> support@reframes.io
          </a>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
};
